import React, { useEffect, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';

import MainPageLayout from './components/MainPageLayout';
import { updateTheme } from './reducer/themeReducer';
import { useDispatch } from 'react-redux';
import AdminPageLayout from './components/AdminPageLayout';
import DoPaymentCheck from './view/TheLaundro/Auth/DoPaymentCheck';
import ManagementUserSettingWrapper from './view/TheLaundro/User/ManagementUserSettingWrapper';
import ForgotPasswordPage from './view/TheLaundro/Auth/ForgotPasswordPage';
import VerifyResetPassword from './view/TheLaundro/Auth/VerifyResetPassword.js';
import VerifyChangeEmail from './view/TheLaundro/User/VerifyChangeEmail.js';

const PrivacyPolicy = lazy(() => import('./view/TheLaundro/Auth/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./view/TheLaundro/Auth/Terms&Conditions'));

// auth
const OperatorLogin = lazy(() => import('./view/TheLaundro/Auth/OperatorLogin.js'));
const OperatorRegister = lazy(() => import('./view/TheLaundro/Auth/OperatorRegister.js'));
const OperatorVerify = lazy(() => import('./view/TheLaundro/Auth/OperatorVerify.js'));

// Admin login
const AdminLogin = lazy(() => import('./view/TheLaundro/Auth/AdminLogin'));

const ClientLogin = lazy(() => import('./view/TheLaundro/Auth/ClientLogin.js'));
const ClientNotificationEngine = lazy(() => import('./view/TheLaundro/User/ClientNotificationEngine.js'));
const DistributorLogin = lazy(() => import('./view/TheLaundro/Auth/DistributorLogin.js'));

// Staff login
const StaffLogin = lazy(() => import('./view/TheLaundro/Auth/staff/StaffLogin'));
const StaffRegister = lazy(() => import('./view/TheLaundro/Auth/staff/StaffRegister'));

// user
const UserProfile = lazy(() => import('./view/TheLaundro/User/UserProfile.js'));
const UserSetting = lazy(() => import('./view/TheLaundro/User/UserSetting.js'));
const UserSettingv2 = lazy(() => import('./view/TheLaundro/User/UserSettingv2.js'));
const Billing = lazy(() => import('./view/TheLaundro/User/Billing.js'));

const UsersListWithRoles = lazy(() => import('./view/TheLaundro/User/UsersListWithRoles'));
const UsersListWithRoles2 = lazy(() => import('./view/TheLaundro/User/UsersListWithRoles2'));
const RoleListPage = lazy(() => import('./view/TheLaundro/User/RoleListPage'));
const NotificationPage = lazy(() => import('./view/TheLaundro/User/NotificationPage'));
const MyOutletsPage = lazy(() => import('./view/TheLaundro/User/MyOutletsPage'));

const SummaryDashboard = lazy(() => import('./view/TheLaundro/Dashboard/SummaryDashboard.js'));
const SummaryDashboardV2 = lazy(() => import('./view/TheLaundro/Dashboard/SummaryDashboardV2.js'));
const SummaryDashboardv3 = lazy(() => import('./view/TheLaundro/Dashboard/SummaryDashboardv3.js'));
const SalesPerformance = lazy(() => import('./view/TheLaundro/Sales Performance/SalesPerformance.js'));
const SalesPerformanceV2 = lazy(() => import('./view/TheLaundro/Sales Performance/SalesPerformanceV2.js'));
const BillSalesPerformance = lazy(() => import('./view/TheLaundro/Sales Performance/BillSalesPerformance.js'));
const BillSalesPerformanceV2 = lazy(() => import('./view/TheLaundro/Sales Performance/BillSalesPerformanceV2.js'));
const MachineUtilization = lazy(() => import('./view/TheLaundro/Machine Utilization/MachineUtilization.js'));
const MachineUtilizationv2 = lazy(() => import('./view/TheLaundro/Machine Utilization/MachineUtilizationv2.js'));
const RemoteActivation = lazy(() => import('./view/TheLaundro/Remote Activation/RemoteActivation.js'));
const RemoteActivationv2 = lazy(() => import('./view/TheLaundro/Remote Activation/RemoteActivationv2.js'));
const UtilityMonitoring = lazy(() => import('./view/TheLaundro/Utility Monitoring/UtilityMonitoring.js'));
const UtilityMonitoringv2 = lazy(() => import('./view/TheLaundro/Utility Monitoring/UtilityMonitoringv2.js'));
// browsable reports
const TransactionsRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/TransactionsRecord.js'));
const KioskRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/KioskRecord.js'));
const EpayRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/EpayRecord.js'));
const DNGRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/DNGRecord.js'));
const ManualPayRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/ManualPayRecord.js'));
const ErrorCodeRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/ErrorCodeRecord.js'));
const BillRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/BillRecord.js'));
const CostingRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/CostingRecord.js'));
const MachineEfficiency = lazy(() => import('./view/TheLaundro/Browsable Reports/MachineEfficiency.js'));
const VendingRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/VendingRecord.js'));
const TerminalRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/TerminalRecord.js'));
const OverallEpaymentRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/OverallEpaymentRecord.js'));
const SubscriptionRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/SubscriptionRecord.js'));
const UnresolvedRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/UnresolveRecord.js'));


const SalesSummary = lazy(() => import('./view/TheLaundro/Browsable Reports/SalesSummary.js'));
const MachineSalesTransactions = lazy(() => import('./view/TheLaundro/Browsable Reports/MachineSalesTransactions.js'));

const HourlySalesTransaction = lazy(() => import('./view/TheLaundro/Browsable Reports/HourlySalesTransaction.js'));
const SalesByMachineType = lazy(() => import('./view/TheLaundro/Browsable Reports/SalesByMachineType.js'));
const SystemErrorReport = lazy(() => import('./view/TheLaundro/Browsable Reports/SystemErrorReport.js'));
const WeeklyReport = lazy(() => import('./view/TheLaundro/Browsable Reports/WeeklyReport.js'));

const SalesSummaryOverall = lazy(() => import('./view/TheLaundro/Browsable Reports/SalesSummaryOverallv2.js'));
const SalesSummaryOverallConsolidated = lazy(() => import('./view/TheLaundro/Browsable Reports/SalesSummaryOverallConsolidated.js'));

//price setting
const PriceList = lazy(() => import('./view/TheLaundro/Others/PriceSetting/PriceList.js'));
const PriceListv2 = lazy(() => import('./view/TheLaundro/Others/PriceSetting/PriceListv2.js'));
const PriceList2 = lazy(() => import('./view/TheLaundro/Inventory/ChemicalStockSetting/PriceList2.js'));
const PriceList2v2 = lazy(() => import('./view/TheLaundro/Inventory/ChemicalStockSetting/PriceList2v2.js'));
const PromotionList = lazy(() => import('./view/TheLaundro/Others/Promotion/PromotionList'));
const PromotionListv2 = lazy(() => import('./view/TheLaundro/Others/Promotion/PromotionListv2'));
const Inventory = lazy(() => import('./view/TheLaundro/Others/Inventory'));
const DropPriceList = lazy(() => import('./view/TheLaundro/Others/DropServiceSetting/PriceList'));
const DropPriceListv2 = lazy(() => import('./view/TheLaundro/Others/DropServiceSetting/PriceListv2'));
const CostingList = lazy(() => import('./view/TheLaundro/Others/CostingSetting/CostingList.js'));
const CostingListv2 = lazy(() => import('./view/TheLaundro/Others/CostingSetting/CostingListv2.js'));
const SalesList = lazy(() => import('./view/TheLaundro/Others/SalesSetting/SalesList.js'));
const SalesListv2 = lazy(() => import('./view/TheLaundro/Others/SalesSetting/SalesListv2.js'));
const CostingLog = lazy(() => import('./view/TheLaundro/Others/CostingSetting/InputCost.js'));
const SalesLog = lazy(() => import('./view/TheLaundro/Others/SalesSetting/InputsSales.js'));
const ImportData = lazy(() => import('./view/TheLaundro/Others/ImportManagement/ImportData.js'));
const ImportDatav2 = lazy(() => import('./view/TheLaundro/Others/ImportManagement/ImportDatav2.js'));
const MachineSettings = lazy(() => import('./view/TheLaundro/Others/MachineSettings/machineSettings.js'));
const MachineSettingsv2 = lazy(() => import('./view/TheLaundro/Others/MachineSettings/machineSettingsv2.js'));

const ReviewDashboard = lazy(() => import('./view/TheLaundro/Review Dashboard/ReviewDashboard'));
const ReviewDashboardv2 = lazy(() => import('./view/TheLaundro/Review Dashboard/ReviewDashboardv2'));

const MachineLogging = lazy(() => import('./view/TheLaundro/Machine Logging/MachineLogging'));
const MachineLoggingv2 = lazy(() => import('./view/TheLaundro/Machine Logging/MachineLoggingv2'));

const DevRoleMainDashboard = lazy(() => import('./view/TheLaundro/Role Management/DevRoleMainDashboard'));
const RoleList = lazy(() => import('./view/TheLaundro/Role Management/RoleList'));
const SyncManagement = lazy(() => import('./view/TheLaundro/Role Management/SyncManagement'));
const KillSwitch = lazy(() => import('./view/TheLaundro/Role Management/KillSwitch'));
const RHBDuitNowManagement = lazy(() => import('./view/TheLaundro/Role Management/RHBDuitNowManagement'));
const SchedulerManagement = lazy(() => import('./view/TheLaundro/Role Management/SchedulerManagement'));
const NewsManagement = lazy(() => import('./view/TheLaundro/Role Management/NewsManagement'));
const PackageManagement = lazy(() => import('./view/TheLaundro/Role Management/PackageManagement'));

const RoleUserList = lazy(() => import('./view/TheLaundro/Role Management/RoleUserList'));
const DistributorsDashboard = lazy(() => import('./view/TheLaundro/Role Management/DistributorsDashboard'));

const OutletOnlineStatus = lazy(() => import('./view/TheLaundro/Dashboard/OutletOnlineStatus'));
const OutletMachines = lazy(() => import('./view/TheLaundro/Dashboard/OutletMachines'));

const CoinCollectionPage = lazy(() => import('./view/TheLaundro/Coin Collection/CoinCollectionPage'));
const CoinCollectionPage2 = lazy(() => import('./view/TheLaundro/Coin Collection/CoinCollectionPageV2'));
const CoinCollectionGroup = lazy(() => import('./view/TheLaundro/Coin Collection/CoinCollectionGroup'));
const StartCollection = lazy(() => import('./view/TheLaundro/Coin Collection/StartCollection'));
const StartCollection2 = lazy(() => import('./view/TheLaundro/Coin Collection/StartCollectionV2'));
const StartStockAdjustment = lazy(() => import('./view/TheLaundro/Coin Collection/StartStockAdjustmentV2'));
const CutoffHistory = lazy(() => import('./view/TheLaundro/Coin Collection/CutoffHistory'));
const CutoffHistoryV2 = lazy(() => import('./view/TheLaundro/Coin Collection/CutoffHistoryV2'));

const CoinCollectionByOutlet = lazy(() => import('./view/TheLaundro/Coin Collection Report/CoinCollectionByOutlet'));
const CoinCollectionByMachines = lazy(() =>
	import('./view/TheLaundro/Coin Collection Report/CoinCollectionByMachines')
);
const BillCollectionReport = lazy(() => import('./view/TheLaundro/Coin Collection Report/BillCollectionReport'));
const InventoryManagement = lazy(() => import('./view/TheLaundro/Inventory/InventoryManagement.js'));
const InventoryManagementv2 = lazy(() => import('./view/TheLaundro/Inventory/InventoryManagementv2.js'));



//Inventory
const Chemicalstock = lazy(() => import('./view/TheLaundro/Inventory/ChemicalStock.js'));
const Inputstock = lazy(() => import('./view/TheLaundro/Inventory/InputStock.js'));
const ChemicalStockSetting = lazy(() => import('./view/TheLaundro/Inventory/ChemicalStockSetting.js'));
const ChemicalBalanceReport = lazy(() => import('./view/TheLaundro/Inventory/ChemicalBalanceReport.js'));

const EInvoice = lazy(() => import('./view/TheLaundro/Others/Einvoice/History.js'));
const ConsolidateEinvoice = lazy(() => import('./view/TheLaundro/Others/Einvoice/ConsolidateHistory.js'));
const EInvoiceEpayment = lazy(() => import('./view/TheLaundro/Others/Einvoice/EpaymentHistory.js'));
const EInvoiceReport = lazy(() => import('./view/TheLaundro/Others/Einvoice/EinvoiceReport.js'));
const EInvoicePackage = lazy(() => import('./view/TheLaundro/Others/Einvoice/PackageHistory.js'));

const ProfitAndLossRecord = lazy(() => import('./view/TheLaundro/Browsable Reports/Profit&LossRecord.js'));
const ProfitLossList = lazy(() => import('./view/TheLaundro/Others/Profit&LossSetting/ProfitLossList.js'));
const ProfitLossListv2 = lazy(() => import('./view/TheLaundro/Others/Profit&LossSetting/ProfitLossListv2.js'));

const BillAcceptorDashboard = lazy(() => import('./view/TheLaundro/Dashboard/BillAcceptorDashboard.js'));
const OutletBillAcceptor = lazy(() => import('./view/TheLaundro/Dashboard/OutletBillAcceptor.js'));

// Offline Dashboard
const OfflineDashboard = lazy(() => import('./view/TheLaundro/Offline Dashboard/OfflineDashboard.js'));

function App() {
	const dispatch = useDispatch();
	useEffect(() => {
		if (window.location.href.includes('localhost')) {
			dispatch(updateTheme('laundro'));

			document.title = 'The Laundro';
			document.querySelector('#my-icon').setAttribute('href', './laundro/favicon.ico');
			document.querySelector('#my-apple-icon').setAttribute('href', './laundro/logo192.png');
			document.querySelector('#my-manifest-placeholder').setAttribute('href', './laundro/manifest.json');
			return;
		}

		if (window.location.href.includes('octopus')) {
			dispatch(updateTheme('octopus'));
			// title replacement

			document.title = 'Washupp';
			// favicon replacement
			document.querySelector('#my-icon').setAttribute('href', './octopus/favicon.ico');
			document.querySelector('#my-apple-icon').setAttribute('href', './octopus/logo192.png');
			// manifest json replace
			document.querySelector('#my-manifest-placeholder').setAttribute('href', './octopus/manifest.json');
		} else if (window.location.href.includes('cuciexpress')) {
			dispatch(updateTheme('cuci'));

			document.title = 'Laundro';
			document.querySelector('#my-icon').setAttribute('href', './cuciexpress/favicon.ico');
			document.querySelector('#my-apple-icon').setAttribute('href', './cuciexpress/logo192.png');
			document.querySelector('#my-manifest-placeholder').setAttribute('href', './cuciexpress/manifest.json');
		} else if (window.location.href.includes('cleanpro')) {
			dispatch(updateTheme('cleanpro'));

			document.title = 'Cleanpro';
			document.querySelector('#my-icon').setAttribute('href', './cleanpro/favicon.ico');
			document.querySelector('#my-apple-icon').setAttribute('href', './cleanpro/logo192.png');
			document.querySelector('#my-manifest-placeholder').setAttribute('href', './cleanpro/manifest.json');
		} else if (window.location.href.includes('washstudio')) {
			dispatch(updateTheme('washstudio'));

			document.title = 'Wash Studio';
			document.querySelector('#my-icon').setAttribute('href', './washstudio/favicon.ico');
			document.querySelector('#my-apple-icon').setAttribute('href', './washstudio/logo192.png');
			document.querySelector('#my-manifest-placeholder').setAttribute('href', './washstudio/manifest.json');
		} else if (window.location.href.includes('aerosparkle')) {
			dispatch(updateTheme('aerosparkle'));

			document.title = 'Aerosparkle';
			document.querySelector('#my-icon').setAttribute('href', './aerosparkle/favicon.ico');
			document.querySelector('#my-apple-icon').setAttribute('href', './aerosparkle/logo192.png');
			document.querySelector('#my-manifest-placeholder').setAttribute('href', './aerosparkle/manifest.json');
		} else if (window.location.href.includes('bubblespulse')) {
			dispatch(updateTheme('bubblespulse'));

			document.title = 'Bubblespulse';
			document.querySelector('#my-icon').setAttribute('href', './bubblespulse/favicon.ico');
			document.querySelector('#my-apple-icon').setAttribute('href', './bubblespulse/logo192.png');
			document.querySelector('#my-manifest-placeholder').setAttribute('href', './bubblespulse/manifest.json');
		} else if (window.location.href.includes('jomcuci')) {
			dispatch(updateTheme('jomcuci'));

			document.title = 'Jom Cuci';
			document.querySelector('#my-icon').setAttribute('href', './jomcuci/favicon.ico');
			document.querySelector('#my-apple-icon').setAttribute('href', './jomcuci/logo192.png');
			document.querySelector('#my-manifest-placeholder').setAttribute('href', './jomcuci/manifest.json');
		} else {
			dispatch(updateTheme('laundro'));

			document.title = 'The Laundro';
			document.querySelector('#my-icon').setAttribute('href', './laundro/favicon.ico');
			document.querySelector('#my-apple-icon').setAttribute('href', './laundro/logo192.png');
			document.querySelector('#my-manifest-placeholder').setAttribute('href', './laundro/manifest.json');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [window.location.href]);

	// console.log("outlets", outlets, error)

	return (
		<div className='App'>
			<Router>
				<Routes>
					<Route exact path='/operator_login' element={<OperatorLogin />} />
					<Route exact path='/operator_register' element={<OperatorRegister />} />
					<Route exact path='/verify' element={<OperatorVerify />} />

					<Route exact path='/admin_login' element={<AdminLogin />} />

					<Route exact path='/distributor_login' element={<DistributorLogin />} />
					<Route exact path='/client_login' element={<ClientLogin />} />

					<Route exact path='/doPayment' element={<DoPaymentCheck />} />

					<Route exact path='/staff_login' element={<StaffLogin />} />
					<Route exact path='/staff_register' element={<StaffRegister />} />

					<Route exact path='/forgot/password' element={<ForgotPasswordPage />} />

					<Route exact path='/password/reset' element={<VerifyResetPassword />} />
					<Route path="/verify-change-email" element={<VerifyChangeEmail />} />
					<Route
						path='/users/operator'
						element={
							<MainPageLayout devSideBars={true}>
								<ManagementUserSettingWrapper>
									<UsersListWithRoles />
								</ManagementUserSettingWrapper>
							</MainPageLayout>
						}
					/>
					<Route
						path='/users/staff'
						element={
							<MainPageLayout devSideBars={true}>
								<ManagementUserSettingWrapper>
									<UsersListWithRoles2 />
								</ManagementUserSettingWrapper>
							</MainPageLayout>
						}
					/>

					<Route
						path='/users/roles'
						element={
							<MainPageLayout devSideBars={true}>
								<ManagementUserSettingWrapper>
									<RoleListPage />
								</ManagementUserSettingWrapper>
							</MainPageLayout>
						}
					/>

					<Route
						path='/users/notification'
						element={
							<MainPageLayout devSideBars={true}>
								<ManagementUserSettingWrapper>
									<NotificationPage />
								</ManagementUserSettingWrapper>
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/mainDashboard'
						element={
							<MainPageLayout devSideBars={true}>
								<SummaryDashboard />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/mainDashboardv3'
						element={
							<MainPageLayout devSideBars={true}>
								<SummaryDashboardv3 />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/outletOnlineStatus'
						element={
							<MainPageLayout devSideBars={true}>
								<OutletOnlineStatus />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/op_profile'
						element={
							<MainPageLayout devSideBars={true}>
								<UserProfile />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/op_setting'
						element={
							<MainPageLayout devSideBars={true}>
								<UserSetting />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/op_settingv2'
						element={
							<MainPageLayout devSideBars={true}>
								<UserSettingv2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/my_outlets'
						element={
							<MainPageLayout devSideBars={true}>
								<MyOutletsPage />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/billing'
						element={
							<MainPageLayout devSideBars={true}>
								<Billing />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/notificationEngine'
						element={
							<MainPageLayout devSideBars={true}>
								<ClientNotificationEngine />
							</MainPageLayout>
						}
					/>
					<Route exact path='/privacypolicy' element={<PrivacyPolicy />} />
					<Route exact path='/terms&conditions' element={<TermsConditions />} />

					<Route
						exact
						path='/annualOverview'
						element={
							<MainPageLayout devSideBars={true}>
								<SalesPerformance />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/SalesPerformanceV2'
						element={
							<MainPageLayout devSideBars={true}>
								<SalesPerformanceV2 />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/billSalesPerformance'
						element={
							<MainPageLayout devSideBars={true}>
								<BillSalesPerformance />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/billSalesPerformanceV2'
						element={
							<MainPageLayout devSideBars={true}>
								<BillSalesPerformanceV2 />
							</MainPageLayout>
						}
					/>
					<Route

						exact
						path='/machineUtilization'
						element={
							<MainPageLayout devSideBars={true}>
								<MachineUtilization />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/machineUtilizationv2'
						element={
							<MainPageLayout devSideBars={true}>
								<MachineUtilizationv2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/remoteActivation'
						element={
							<MainPageLayout devSideBars={true}>
								<RemoteActivation />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/remoteActivationv2'
						element={
							<MainPageLayout devSideBars={true}>
								<RemoteActivationv2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/priceList'
						element={
							<MainPageLayout devSideBars={true}>
								<PriceList />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/priceListv2'
						element={
							<MainPageLayout devSideBars={true}>
								<PriceListv2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/droppriceList'
						element={
							<MainPageLayout devSideBars={true}>
								<DropPriceList />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/droppriceListv2'
						element={
							<MainPageLayout devSideBars={true}>
								<DropPriceListv2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/priceList2'
						element={
							<MainPageLayout devSideBars={true}>
								<PriceList2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/priceList2v2'
						element={
							<MainPageLayout devSideBars={true}>
								<PriceList2v2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/costingList'
						element={
							<MainPageLayout devSideBars={true}>
								<CostingList />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/costingListv2'
						element={
							<MainPageLayout devSideBars={true}>
								<CostingListv2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/costingLog'
						element={
							<MainPageLayout devSideBars={true}>
								<CostingLog />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/salesLog'
						element={
							<MainPageLayout devSideBars={true}>
								<SalesLog />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/salesList'
						element={
							<MainPageLayout devSideBars={true}>
								<SalesList />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/salesListv2'
						element={
							<MainPageLayout devSideBars={true}>
								<SalesListv2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/promotion'
						element={
							<MainPageLayout devSideBars={true}>
								<PromotionList />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/promotionv2'
						element={
							<MainPageLayout devSideBars={true}>
								<PromotionListv2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/importData'
						element={
							<MainPageLayout devSideBars={true}>
								<ImportData />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/importDatav2'
						element={
							<MainPageLayout devSideBars={true}>
								<ImportDatav2 />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/utilityMonitoring'
						element={
							<MainPageLayout devSideBars={true}>
								<UtilityMonitoring />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/utilityMonitoringv2'
						element={
							<MainPageLayout devSideBars={true}>
								<UtilityMonitoringv2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/transactions_record'
						element={
							<MainPageLayout devSideBars={true}>
								<TransactionsRecord />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/kiosk_record'
						element={
							<MainPageLayout devSideBars={true}>
								<KioskRecord />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/sales_transaction_record'
						element={
							<MainPageLayout devSideBars={true}>
								<MachineSalesTransactions />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/hourly_sales_transaction'
						element={
							<MainPageLayout devSideBars={true}>
								<HourlySalesTransaction />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/epayment_record'
						element={
							<MainPageLayout devSideBars={true}>
								<EpayRecord />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/unresolve_record'
						element={
							<MainPageLayout devSideBars={true}>
								<UnresolvedRecord />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/terminal_record'
						element={
							<MainPageLayout devSideBars={true}>
								<TerminalRecord />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/overall_epayment_record'
						element={
							<MainPageLayout devSideBars={true}>
								<OverallEpaymentRecord />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/dng_record'
						element={
							<MainPageLayout devSideBars={true}>
								<DNGRecord />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/costing_record'
						element={
							<MainPageLayout devSideBars={true}>
								<CostingRecord />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/manualPay_record'
						element={
							<MainPageLayout devSideBars={true}>
								<ManualPayRecord />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/errorcode_record'
						element={
							<MainPageLayout devSideBars={true}>
								<ErrorCodeRecord />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/bill_record'
						element={
							<MainPageLayout devSideBars={true}>
								<BillRecord />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/vending_record'
						element={
							<MainPageLayout devSideBars={true}>
								<VendingRecord />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/sales_summary'
						element={
							<MainPageLayout devSideBars={true}>
								<SalesSummary />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/salesByMachineType'
						element={
							<MainPageLayout devSideBars={true}>
								<SalesByMachineType />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/systemErrorReport'
						element={
							<MainPageLayout devSideBars={true}>
								<SystemErrorReport />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/reviewDashboard'
						element={
							<MainPageLayout devSideBars={true}>
								<ReviewDashboard />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/reviewDashboardv2'
						element={
							<MainPageLayout devSideBars={true}>
								<ReviewDashboardv2 />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/inventory'
						element={
							<MainPageLayout devSideBars={true}>
								<Inventory />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/machineLog'
						element={
							<MainPageLayout devSideBars={true}>
								<MachineLogging />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/machineLogv2'
						element={
							<MainPageLayout devSideBars={true}>
								<MachineLoggingv2 />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/role/dashboard'
						element={
							<AdminPageLayout devSideBars={false}>
								<DevRoleMainDashboard />
							</AdminPageLayout>
						}
					/>
					<Route
						exact
						path='/roleList'
						element={
							<AdminPageLayout devSideBars={false}>
								<RoleList />
							</AdminPageLayout>
						}
					/>
					<Route
						exact
						path='/userList'
						element={
							<AdminPageLayout devSideBars={false}>
								<RoleUserList />
							</AdminPageLayout>
						}
					/>
					<Route
						exact
						path='/syncList'
						element={
							<AdminPageLayout devSideBars={false}>
								<SyncManagement />
							</AdminPageLayout>
						}
					/>

					<Route
						exact
						path='/distributors/:clientId'
						element={
							<AdminPageLayout devSideBars={false}>
								<DistributorsDashboard />
							</AdminPageLayout>
						}
					/>

					<Route
						exact
						path='/killSwitch'
						element={
							<AdminPageLayout devSideBars={false}>
								<KillSwitch />
							</AdminPageLayout>
						}
					/>

					<Route
						exact
						path='/coin/collection'
						element={
							<MainPageLayout devSideBars={true}>
								<CoinCollectionPage />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/CoinCollectionPage2'
						element={
							<MainPageLayout devSideBars={true}>
								<CoinCollectionPage2 />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/coin/collection/group'
						element={
							<MainPageLayout devSideBars={true}>
								<CoinCollectionGroup />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/coin/collection/start'
						element={
							<MainPageLayout devSideBars={true}>
								<StartCollection />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/coin/collection/start2'
						element={
							<MainPageLayout devSideBars={true}>
								<StartCollection2 />
							</MainPageLayout>
						}
					/>
					
					<Route
						exact
						path='/coin/collection/stock-adjustment'
						element={
							<MainPageLayout devSideBars={true}>
								<StartStockAdjustment />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/coin/collection/history'
						element={
							<MainPageLayout devSideBars={true}>
								<CutoffHistory />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/coin/collection/historyv2'
						element={
							<MainPageLayout devSideBars={true}>
								<CutoffHistoryV2 />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/coin/collection/outlet'
						element={
							<MainPageLayout devSideBars={true}>
								<CoinCollectionByOutlet />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/coin/collection/machine'
						element={
							<MainPageLayout devSideBars={true}>
								<CoinCollectionByMachines />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/collection/bill'
						element={
							<MainPageLayout devSideBars={true}>
								<BillCollectionReport />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/inventory/management'
						element={
							<MainPageLayout devSideBars={true}>
								<InventoryManagement />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/inventorymanagementv2'
						element={
							<MainPageLayout devSideBars={true}>
								<InventoryManagementv2 />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/inventory/chemicalstock'
						element={
							<MainPageLayout devSideBars={true}>
								<Chemicalstock />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/inventory/inputstock'
						element={
							<MainPageLayout devSideBars={true}>
								<Inputstock />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/inventory/chemicalstocksetting'
						element={
							<MainPageLayout devSideBars={true}>
								<ChemicalStockSetting />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/inventory/chemicalbalancereport'
						element={
							<MainPageLayout devSideBars={true}>
								<ChemicalBalanceReport />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/sales/overall'
						element={
							<MainPageLayout devSideBars={true}>
								<SalesSummaryOverall />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/sales/overallv2'
						element={
							<MainPageLayout devSideBars={true}>
								<SalesSummaryOverallConsolidated />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/weekly/report'
						element={
							<MainPageLayout devSideBars={true}>
								<WeeklyReport />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/machine/efficiency'
						element={
							<MainPageLayout devSideBars={true}>
								<MachineEfficiency />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/subscription_record'
						element={
							<MainPageLayout devSideBars={true}>
								<SubscriptionRecord />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/machineSettings'
						element={
							<MainPageLayout devSideBars={true}>
								<MachineSettings />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/machineSettingsv2'
						element={
							<MainPageLayout devSideBars={true}>
								<MachineSettingsv2 />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/outlet-machines'
						element={
							<MainPageLayout devSideBars={true}>
								<OutletMachines />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/mainDashboardv2'
						element={
							<MainPageLayout devSideBars={true}>
								<SummaryDashboardV2 />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/rhbduitnow'
						element={
							<AdminPageLayout devSideBars={false}>
								<RHBDuitNowManagement />
							</AdminPageLayout>
						}
					/>

				<Route
					exact
					path='/newsManagement'
					element={
						<AdminPageLayout devSideBars={false}>
							<NewsManagement />
							</AdminPageLayout>
						}
					/>
					
					<Route
						exact
						path='/packageManagement'
						element={
							<AdminPageLayout devSideBars={false}>
								<PackageManagement />
							</AdminPageLayout>
						}
					/>

					<Route
						exact
						path='/schedulerManagement'
						element={
							<AdminPageLayout devSideBars={false}>
								<SchedulerManagement />
							</AdminPageLayout>
						}
					/>

					<Route
						exact
						path='/invoice/validation'
						element={
							<MainPageLayout devSideBars={true}>
								<EInvoice />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/invoice/consolidate'
						element={
							<MainPageLayout devSideBars={true}>
								<ConsolidateEinvoice />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/invoice/epayvalidation'
						element={
							<MainPageLayout devSideBars={true}>
								<EInvoiceEpayment />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/invoice/report'
						element={
							<MainPageLayout devSideBars={true}>
								<EInvoiceReport />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/invoice/packagevalidation'
						element={
							<MainPageLayout devSideBars={true}>
								<EInvoicePackage />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/profit&loss'
						element={
							<MainPageLayout devSideBars={true}>
								<ProfitLossList />
							</MainPageLayout>
						}
					/>
					<Route
						exact
						path='/profit&lossv2'
						element={
							<MainPageLayout devSideBars={true}>
								<ProfitLossListv2 />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/profitloss_record'
						element={
							<MainPageLayout devSideBars={true}>
								<ProfitAndLossRecord />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/billacceptordashboard'
						element={
							<MainPageLayout devSideBars={true}>
								<BillAcceptorDashboard />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/outletbillacceptor'
						element={
							<MainPageLayout devSideBars={true}>
								<OutletBillAcceptor />
							</MainPageLayout>
						}
					/>

					<Route
						exact
						path='/offline_dashboard'
						element={
							<MainPageLayout devSideBars={true}>
								<OfflineDashboard />
							</MainPageLayout>
						}
					/>

					<Route path='*' element={<Navigate to='/mainDashboard' />}></Route>
				</Routes>
			</Router>
		</div>
	);
}

export default App;
